import IndicatorButton from 'components/indicator/IndicatorButton';
import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import GoogleAnalyticsHelper from 'helpers/GoogleAnalyticsHelper';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import usePageTracking from 'hooks/usePageTracking';
import Error404 from 'pages/error/Error404';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import TagManager from 'react-gtm-module';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { DiagnosisContentState } from 'stores/atoms/diagnosisContentState';
import { DiagnosisRespondentState } from 'stores/atoms/diagnosisRespondentState';
import { DiagnosisPublishType } from 'types/diagnosisPublishType';

/**
 *　トップ画面
 */
export default function DiagnosisTop(props: { preview?: boolean }): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { diagnosisCd } = useParams();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const [diagnosisContentState, setContentState] = useRecoilState(DiagnosisContentState);
  const [diagnosisRespondentState, setDiagnosisRespondentState] =
    useRecoilState(DiagnosisRespondentState);

  const { postDiagnosisDisplay, postDiagnosisParticipate } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [notFoundState, setNotFoundState] = useState(false);
  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    _initState();
  }, []);
  useEffect(() => {
    if (diagnosisContentState?.diagnosis?.gtmId && !diagnosisRespondentState?.preview) {
      TagManager.initialize({ gtmId: diagnosisContentState.diagnosis.gtmId });
    }
  }, [diagnosisContentState, diagnosisRespondentState]);
  // GTMトラッキング
  usePageTracking(
    diagnosisContentState?.diagnosis?.gtmId,
    location.pathname,
    diagnosisRespondentState?.preview
  );

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　スタートボタン押下時の処理
   */
  const onSubmit = () =>
    AppHelper.submit(processing, setProcessing, async () => {
      try {
        // GA4に送信
        if (!props.preview) {
          GoogleAnalyticsHelper.event('conversion', 'branq_start', '診断開始');
        }
        // メールアドレス入力タイミングが「診断開始前」の場合はメールアドレス登録画面に遷移
        if (
          diagnosisContentState.diagnosis.respondentMailDiv ==
          DivConst.items.diagnosisRespondentMail.beginning.value
        ) {
          setDiagnosisRespondentState({
            ...diagnosisRespondentState,
            page: 'form',
          });
          navigate(`/diagnosis/${diagnosisCd}/form/1`);
        }
        // 上記以外は診断参加APIを呼び出し、設問画面に遷移
        else {
          if (!props.preview) {
            await postDiagnosisParticipate({
              diagnosisId: diagnosisContentState.diagnosis.id,
            });
          }
          setDiagnosisRespondentState({
            ...diagnosisRespondentState,
            page: 'question',
          });
          navigate(`/diagnosis/${diagnosisCd}/question/1`);
        }
      } catch (e) {
        showBoundary(e);
      }
    });

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _initState = async () => {
    try {
      // URLの取得
      const url = `${config.s3.bucket.staticContents.endpoint}/diagnosis/${
        props.preview ? 'preview' : 'production'
      }/${diagnosisCd}.json`;

      // 診断データの取得
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
      });
      const json: DiagnosisPublishType = await response.json();
      _patchJson(json);
      setInitialized(true);

      // bodyのクラスを設定
      document.body.classList.add(AppHelper.getDesignClassName(json.diagnosis.diagnosisTemplateId));

      // 診断表示APIの呼び出し
      if (!props.preview) {
        postDiagnosisDisplay({
          diagnosisId: json.diagnosis.id,
        });
      }

      // 診断回答者ステートの初期化
      setDiagnosisRespondentState({
        id: null,
        page: 'top',
        answers: [],
        respondentForm: null,
        preview: props.preview || false,
      });

      // GA4に送信
      if (!props.preview) {
        GoogleAnalyticsHelper.initialize([
          config.ga4.trackingId,
          json.company.apiKeyGoogleAnalytics,
        ]);
        GoogleAnalyticsHelper.pageview(`/diagnosis/${diagnosisCd}`, json.diagnosis.title);
      }
    } catch (e) {
      setNotFoundState(true);
    }
  };

  /* JSON定義のパッチ処理（バージョンまたぎJSON対応） */
  const _patchJson = async (json: DiagnosisPublishType) => {
    const patchedJson = json;
    if (!json.diagnosis.respondentFields) {
      // 取得フィールドがない状態（項目追加対応前の場合）
      // メールアドレスのフィールドを追加
      patchedJson.diagnosis.respondentFields = [
        {
          getFlag:
            json.diagnosis.respondentMailDiv !== DivConst.items.diagnosisRespondentMail.none.value,
          id: 0,
          label: null,
          maxLength: null,
          no: 1,
          placeholder: null,
          respondentCustomFieldDiv: null,
          respondentCustomFieldInputDiv: null,
          respondentFieldDiv: 10,
          respondentStandardFieldDiv: 10,
        },
      ];
    }
    setContentState(patchedJson);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {notFoundState && <Error404 />}

      {initialized && !notFoundState && (
        <>
          <main>
            <section className="section-common-quiz">
              <div className="container">
                <div className="card card-quiz">
                  <div className="card-body">
                    {/* 診断のタイトル */}
                    {!diagnosisContentState.diagnosis.titleHiddenFlag && (
                      <h1 className="title-section">{diagnosisContentState.diagnosis.title}</h1>
                    )}
                    {/* 表紙の画像 */}
                    {diagnosisContentState.diagnosis?.imageKey && (
                      <img
                        src={AppHelper.getImageUrl(diagnosisContentState.diagnosis.imageKey)}
                        className="img-fluid d-block mx-auto img-lg"
                      />
                    )}
                    {/* 説明文 */}
                    <pre>{diagnosisContentState.diagnosis.description}</pre>
                    {/* 次へボタン */}
                    <div className="btn-container">
                      <span className="ql-button">
                        <a onClick={onSubmit}>
                          <IndicatorButton visible={processing} />
                          今すぐスタート
                        </a>
                      </span>
                    </div>
                    {/* 利用規約、プライバシーポリシー */}
                    <p className="text-center">
                      続行することで、
                      <a href="https://branq.jp/terms/" target="_blank">
                        利用規約
                      </a>
                      に同意し、
                      <br />
                      <a href={diagnosisContentState.company.privacyPolicyUrl} target="_blank">
                        プライバシーポリシー
                      </a>
                      を確認したものとみなされます。
                    </p>
                    <p className="copyright">©{diagnosisContentState.company.copyright}</p>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </>
      )}
    </>
  );
}
